import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { MessageSquare, Heart, Clock, Smile, ShieldCheck, Zap, Star, Headphones } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Guia de Atendimento ao Cliente IPTV", href: "/blog/guia-atendimento-cliente-iptv" }
  ];

  const sections = [
    {
      title: "1. A Primeira Impressão: O Atendimento na Venda",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            No mercado de IPTV, o produto é digital e invisível até que o cliente o instale. Por isso, nas primeiras interações, o seu produto é o seu atendimento. Um atendimento rápido, educado e tecnicamente preciso transmite a segurança que o cliente precisa para confiar e fazer o pagamento.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            O segredo não é apenas vender, mas realizar uma consultoria. Entenda qual dispositivo o cliente usa, qual a velocidade da internet dele e o que ele mais gosta de assistir. Isso permite que você ofereça a solução perfeita, aumentando drasticamente as chances de conversão.
          </p>
        </div>
      )
    },
    {
      title: "2. Pilares do Atendimento de Elite",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-black/40 p-6 rounded-2xl border border-white/5 space-y-3">
              <Clock className="w-8 h-8 text-electric" />
              <h4 className="text-white font-bold">Velocidade</h4>
              <p className="text-sm text-muted-foreground">O cliente de streaming é imediatista. Se você demora 2 horas para responder, ele já comprou com o concorrente.</p>
            </div>
            <div className="bg-black/40 p-6 rounded-2xl border border-white/5 space-y-3">
              <ShieldCheck className="w-8 h-8 text-amber-500" />
              <h4 className="text-white font-bold">Precisão Técnica</h4>
              <p className="text-sm text-muted-foreground">Saiba explicar como instalar em qualquer TV. Erros técnicos no suporte geram desconfiança e cancelamentos.</p>
            </div>
            <div className="bg-black/40 p-6 rounded-2xl border border-white/5 space-y-3">
              <Heart className="w-8 h-8 text-red-500" />
              <h4 className="text-white font-bold">Empatia</h4>
              <p className="text-sm text-muted-foreground">Lide com calma com clientes que não têm facilidade com tecnologia. A paciência aqui gera fidelidade eterna.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Lidando com Suporte e Crises",
      content: (
        <div className="prose prose-invert max-w-none space-y-6">
          <p className="text-muted-foreground">Nenhum serviço é 100% perfeito o tempo todo. O que define seu sucesso é como você reage quando algo sai do esperado:</p>
          <div className="space-y-4">
            <div className="bg-white/5 p-6 rounded-xl border border-white/10">
              <h4 className="text-white font-bold mb-2 flex items-center gap-2"><Star className="text-electric w-5 h-5" /> Transparência Total</h4>
              <p className="text-muted-foreground">Se houver uma manutenção programada ou uma instabilidade geral, avise seus clientes antes que eles venham reclamar. Proatividade resolve 80% dos problemas de imagem.</p>
            </div>
            <div className="bg-white/5 p-6 rounded-xl border border-white/10">
              <h4 className="text-white font-bold mb-2 flex items-center gap-2"><Smile className="text-electric w-5 h-5" /> Linguagem Positiva</h4>
              <p className="text-muted-foreground">Em vez de dizer "O sistema está caído", diga "Estamos realizando uma atualização de performance para garantir que seu sinal fique ainda melhor. Voltaremos em instantes".</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Ferramentas para Escalar o Atendimento",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Para atender 500 clientes sozinho, você precisa de método:</p>
          <ul className="space-y-4 list-none p-0">
            <li className="flex gap-4 items-start">
              <div className="bg-electric/20 p-2 rounded-lg text-electric"><MessageSquare className="w-5 h-5" /></div>
              <div>
                <h5 className="font-bold text-white">Mensagens Rápidas (Shortcuts)</h5>
                <p className="text-sm text-muted-foreground">Tenha tutoriais prontos, links de pagamento e boas-vindas salvos para envio imediato.</p>
              </div>
            </li>
            <li className="flex gap-4 items-start">
              <div className="bg-electric/20 p-2 rounded-lg text-electric"><Headphones className="w-5 h-5" /></div>
              <div>
                <h5 className="font-bold text-white">Status do WhatsApp</h5>
                <p className="text-sm text-muted-foreground">Use o status para dar avisos gerais, novidades de filmes e séries e informativos técnicos. É um canal de suporte em massa gratuito.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias IPTV"
        title="Guia de Atendimento ao Cliente IPTV: A Arte da Fidelização"
        subtitle="Saiba como transformar seu WhatsApp em uma máquina de vendas e suporte de elite, garantindo clientes fiéis e satisfeitos por muito mais tempo."
        heroImage="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "No mercado de IPTV, onde a concorrência é vasta e os produtos muitas vezes parecidos, o seu atendimento é o que define o seu preço e a sua longevidade. Um revendedor que atende bem pode cobrar mais caro e ainda assim ter mais clientes do que aquele que briga por preço, mas não dá suporte.",
          "Neste guia, vamos explorar as técnicas de comunicação que os maiores players do mercado utilizam para encantar seus usuários. Vamos falar sobre gatilhos mentais de venda, como lidar com clientes difíceis e como organizar seu suporte para que ele seja eficiente sem consumir 24 horas do seu dia.",
          "Atender bem não é apenas ser educado; é ser estratégico. É saber guiar o cliente pela jornada de compra e garantir que ele se sinta cuidado após o pagamento. Vamos transformar seu suporte em um diferencial competitivo imbatível."
        ]}
        breadcrumbs={breadcrumbs}
        faq={[
          {
            question: "Como lidar com um cliente grosseiro?",
            answer: "Mantenha o profissionalismo. Não leve para o lado pessoal. Responda de forma técnica e objetiva. Se o cliente for abusivo, avalie se vale a pena mantê-lo na sua carteira."
          },
          {
            question: "Qual o melhor horário para dar suporte?",
            answer: "Defina um horário comercial claro, mas esteja preparado para emergências em horários de grandes jogos ou estreias. Ferramentas de automação ajudam a gerenciar as expectativas fora do horário."
          }
        ]}
        benefits={[
          {
            title: "Retenção Máxima",
            description: "Clientes bem atendidos raramente trocam de fornecedor, garantindo seu faturamento mensal.",
            icon: <Heart className="w-6 h-6" />
          },
          {
            title: "Indicações Orgânicas",
            description: "O bom atendimento é o marketing mais barato que existe. Seus clientes vão te vender para os amigos.",
            icon: <Star className="w-6 h-6" />
          }
        ]}
        howItWorks={[
          {
            title: "Escuta Ativa",
            description: "Entenda a dor do cliente antes de oferecer a solução. Isso gera conexão e confiança imediata."
          },
          {
            title: "Suporte Resolutivo",
            description: "Não dê voltas. Vá direto ao ponto e resolva o problema técnico com clareza e rapidez."
          }
        ]}
        diferenciais={[
          {
            title: "Humanização no Digital",
            description: "Mesmo usando automações, mantemos o toque humano que faz o cliente se sentir especial.",
            icon: <Smile className="w-6 h-6" />
          }
        ]}
        sections={sections}
        ctaTitle="🌟 Quer Elevar o Nível do seu Atendimento?"
        ctaDescription="Conte com a infraestrutura da P2 PRO para oferecer o melhor serviço e foque o seu tempo em encantar seus clientes com um suporte de elite."
        ctaButtonText="Quero me Tornar um Revendedor de Elite"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/guia-atendimento-cliente-iptv")({
  head: () => ({
    meta: [
      { title: "Guia de Atendimento ao Cliente IPTV: A Arte da Fidelização" },
      { name: "description", content: "Aprenda as melhores técnicas de atendimento para revenda IPTV. Melhore sua conversão, fidelize clientes e organize seu suporte técnico com profissionalismo." },
    ],
  }),
  component: ArticlePage,
});